// Executive Audio Engine: Polyphonic Orchestral Waiting Tones & Incoming Call Ringer
// High-fidelity Web Audio API engine for 0-latency, rich acoustic soundscapes.

export const WAITING_TONE_PRESETS = {
  symphonic: {
    id: 'symphonic',
    name: 'Symphonic Grand',
    badge: 'Executive',
    icon: '🎻',
    instruments: 'Grand Piano & Lush Strings',
    subtitle: 'Warm, majestic luxury chord progression (C - G - Am - Fmaj7)'
  },
  cinematic: {
    id: 'cinematic',
    name: 'Cinematic Cello',
    badge: 'Modern Luxury',
    icon: '🎬',
    instruments: 'Deep Cello & Airy Harmonic Fifths',
    subtitle: 'Evocative, deep, ambient cinematic swell (Dm - Bb - F - C)'
  },
  acoustic: {
    id: 'acoustic',
    name: 'Acoustic Harmony',
    badge: 'Warm & Inviting',
    icon: '🎸',
    instruments: 'Acoustic Guitar & Soft Strings Pad',
    subtitle: 'Gentle, uplifting acoustic harmony for welcoming clients'
  },
  minimalist: {
    id: 'minimalist',
    name: 'Glass Bells & Chimes',
    badge: 'Clean Minimal',
    icon: '🔔',
    instruments: 'Celesta, Glass Bells & Zen Chimes',
    subtitle: 'Understated, crystal-clear high-tech luxury chimes'
  }
};

export const getSavedWaitingTone = () => {
  if (typeof window === 'undefined') return 'symphonic';
  return localStorage.getItem('epicedge_waiting_tone') || 'symphonic';
};

export const saveWaitingToneSetting = (toneKey) => {
  if (typeof window === 'undefined') return;
  const validKey = WAITING_TONE_PRESETS[toneKey] ? toneKey : 'symphonic';
  localStorage.setItem('epicedge_waiting_tone', validKey);
  window.dispatchEvent(new CustomEvent('epic_waiting_tone_changed', {
    detail: { toneKey: validKey }
  }));
};

class OrchestralWaitingPlayer {
  constructor() {
    this.audioCtx = null;
    this.masterGain = null;
    this.isPlaying = false;
    this.timerId = null;
    this.stopTimeout = null;
    this.activeNodes = [];
    this.currentPreset = 'symphonic';
  }

  ensureContext() {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;

    if (!this.audioCtx || this.audioCtx.state === 'closed') {
      this.audioCtx = new AudioCtx();
    }
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume().catch(() => {});
    }

    if (!this.masterGain) {
      this.masterGain = this.audioCtx.createGain();
      this.masterGain.gain.setValueAtTime(0.3, this.audioCtx.currentTime);
      this.masterGain.connect(this.audioCtx.destination);
    }
    return this.audioCtx;
  }

  // Play a resonant harmonic note
  playNote(freq, startTime, duration, type = 'sine', volume = 0.22, detune = 0) {
    if (!this.audioCtx || !this.masterGain) return;

    try {
      const osc = this.audioCtx.createOscillator();
      const gain = this.audioCtx.createGain();

      osc.type = type;
      osc.frequency.setValueAtTime(freq, startTime);
      if (detune) osc.detune.setValueAtTime(detune, startTime);

      const attack = Math.min(0.2, duration * 0.15);
      gain.gain.setValueAtTime(0.001, startTime);
      gain.gain.exponentialRampToValueAtTime(volume, startTime + attack);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

      osc.connect(gain);
      gain.connect(this.masterGain);

      osc.start(startTime);
      osc.stop(startTime + duration + 0.05);

      this.activeNodes.push(osc);
      setTimeout(() => {
        const i = this.activeNodes.indexOf(osc);
        if (i > -1) this.activeNodes.splice(i, 1);
      }, (duration + 0.2) * 1000);
    } catch (e) {
      console.warn('Note play error:', e);
    }
  }

  playPhrase(presetKey) {
    const ctx = this.ensureContext();
    if (!ctx) return;
    const now = ctx.currentTime + 0.05;

    if (presetKey === 'cinematic') {
      // Cinematic Cello: Dm -> Bb -> F -> C
      const progression = [
        { bass: 73.42, notes: [146.83, 220.00, 293.66, 349.23], dur: 2.8 },  // Dm
        { bass: 58.27, notes: [116.54, 233.08, 293.66, 349.23], dur: 2.8 },  // Bb
        { bass: 87.31, notes: [174.61, 261.63, 349.23, 440.00], dur: 2.8 },  // F
        { bass: 65.41, notes: [130.81, 196.00, 261.63, 329.63], dur: 2.8 }   // C
      ];

      progression.forEach((chord, cIdx) => {
        const time = now + (cIdx * 2.5);
        // Rich deep Cello fundamental
        this.playNote(chord.bass, time, chord.dur, 'sawtooth', 0.18, 0);
        this.playNote(chord.bass * 2, time + 0.02, chord.dur, 'triangle', 0.22, 2);

        // Strings harmony
        chord.notes.forEach((f, nIdx) => {
          this.playNote(f, time + (nIdx * 0.08), chord.dur - 0.2, 'sine', 0.14, nIdx % 2 === 0 ? 4 : -4);
          this.playNote(f, time + (nIdx * 0.08), chord.dur - 0.2, 'triangle', 0.08, 0);
        });
      });
    } else if (presetKey === 'acoustic') {
      // Acoustic Harmony: G -> Cadd9 -> Em7 -> D
      const progression = [
        [98.00, 196.00, 293.66, 392.00, 493.88],  // G
        [130.81, 196.00, 261.63, 329.63, 392.00], // Cadd9
        [82.41, 164.81, 246.94, 329.63, 392.00],  // Em7
        [73.42, 146.83, 220.00, 293.66, 369.99]   // D
      ];

      progression.forEach((chord, cIdx) => {
        const chordTime = now + (cIdx * 2.4);
        // Warm plucked notes
        chord.forEach((freq, nIdx) => {
          const noteTime = chordTime + (nIdx * 0.18);
          this.playNote(freq, noteTime, 2.0, 'triangle', 0.22, 3);
          this.playNote(freq * 2, noteTime, 1.2, 'sine', 0.12, -3);
        });
        // Bass tone
        this.playNote(chord[0], chordTime, 2.4, 'sine', 0.25, 0);
      });
    } else if (presetKey === 'minimalist') {
      // Glass Bells & Chimes
      const phrases = [
        [523.25, 659.25, 783.99, 1046.50], // C5, E5, G5, C6
        [440.00, 523.25, 659.25, 880.00],  // A4, C5, E5, A5
        [392.00, 493.88, 587.33, 783.99],  // G4, B4, D5, G5
        [349.23, 440.00, 523.25, 698.46]   // F4, A4, C5, F5
      ];

      phrases.forEach((phrase, pIdx) => {
        const pTime = now + (pIdx * 2.5);
        phrase.forEach((freq, idx) => {
          const noteTime = pTime + (idx * 0.25);
          this.playNote(freq, noteTime, 2.2, 'sine', 0.24, 0);
          this.playNote(freq * 2, noteTime, 1.2, 'triangle', 0.08, 0);
        });
        this.playNote(phrase[0] / 2, pTime, 2.4, 'sine', 0.18, 0);
      });
    } else {
      // Default: Symphonic Grand (C -> G -> Am -> Fmaj7)
      const progression = [
        { root: 65.41, notes: [130.81, 196.00, 261.63, 329.63, 392.00, 523.25], dur: 2.6 }, // C
        { root: 49.00, notes: [98.00, 196.00, 246.94, 293.66, 392.00, 493.88], dur: 2.6 },  // G
        { root: 55.00, notes: [110.00, 164.81, 220.00, 261.63, 329.63, 440.00], dur: 2.6 }, // Am
        { root: 43.65, notes: [87.31, 174.61, 220.00, 261.63, 329.63, 523.25], dur: 2.6 }  // Fmaj7
      ];

      progression.forEach((chord, cIdx) => {
        const chordTime = now + (cIdx * 2.4);

        // Grand Piano Arpeggiated Notes
        chord.notes.forEach((freq, nIdx) => {
          const noteTime = chordTime + (nIdx * 0.14);
          this.playNote(freq, noteTime, 2.0, 'sine', 0.20, 2);
          this.playNote(freq, noteTime, 1.6, 'triangle', 0.12, -2);
        });

        // Deep Bass resonance
        this.playNote(chord.root, chordTime, 2.5, 'sawtooth', 0.14, 0);
        this.playNote(chord.root * 2, chordTime, 2.5, 'sine', 0.22, 0);
      });
    }
  }

  start(presetKey = null) {
    this.stop(); // Clear any previous active sounds immediately

    const ctx = this.ensureContext();
    if (!ctx) return;

    this.isPlaying = true;
    const toneKey = presetKey || getSavedWaitingTone();
    this.currentPreset = toneKey;

    // Reset volume to audible level immediately
    if (this.masterGain) {
      this.masterGain.gain.setValueAtTime(0.3, ctx.currentTime);
    }

    // Play right away!
    this.playPhrase(toneKey);

    // Loop every 10 seconds
    this.timerId = setInterval(() => {
      if (this.isPlaying) {
        this.playPhrase(this.currentPreset);
      }
    }, 10000);
  }

  duck(duckLevel = 0.06) {
    if (!this.audioCtx || !this.masterGain || !this.isPlaying) return;
    try {
      this.masterGain.gain.setValueAtTime(duckLevel, this.audioCtx.currentTime);
    } catch (e) {}
  }

  unduck() {
    if (!this.audioCtx || !this.masterGain || !this.isPlaying) return;
    try {
      this.masterGain.gain.setValueAtTime(0.3, this.audioCtx.currentTime);
    } catch (e) {}
  }

  stop() {
    this.isPlaying = false;

    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
    if (this.stopTimeout) {
      clearTimeout(this.stopTimeout);
      this.stopTimeout = null;
    }

    // Stop active audio oscillators immediately
    this.activeNodes.forEach(node => {
      try {
        node.stop();
        node.disconnect();
      } catch (e) {}
    });
    this.activeNodes = [];

    if (this.audioCtx && this.masterGain) {
      try {
        this.masterGain.gain.setValueAtTime(0.001, this.audioCtx.currentTime);
      } catch (e) {}
    }
  }
}

export const waitingTonePlayer = new OrchestralWaitingPlayer();

export const testPlayWaitingTone = (presetKey = 'symphonic') => {
  waitingTonePlayer.stop();
  waitingTonePlayer.start(presetKey);

  // Auto-stop preview after 6 seconds
  setTimeout(() => {
    waitingTonePlayer.stop();
  }, 6000);
};

// Standard ringer for admin incoming call
class CallRingtone {
  constructor() {
    this.audioCtx = null;
    this.intervalId = null;
    this.isPlaying = false;
  }

  playChime() {
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      if (!this.audioCtx) {
        this.audioCtx = new AudioCtx();
      }
      if (this.audioCtx.state === 'suspended') {
        this.audioCtx.resume().catch(() => {});
      }

      const now = this.audioCtx.currentTime;

      // Note 1: 440Hz (A4)
      const osc1 = this.audioCtx.createOscillator();
      const gain1 = this.audioCtx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(440, now);
      gain1.gain.setValueAtTime(0.2, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      osc1.connect(gain1);
      gain1.connect(this.audioCtx.destination);
      osc1.start(now);
      osc1.stop(now + 0.35);

      // Note 2: 660Hz (E5)
      const osc2 = this.audioCtx.createOscillator();
      const gain2 = this.audioCtx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(660, now + 0.12);
      gain2.gain.setValueAtTime(0.25, now + 0.12);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      osc2.connect(gain2);
      gain2.connect(this.audioCtx.destination);
      osc2.start(now + 0.12);
      osc2.stop(now + 0.5);

      // Note 3: 880Hz (A5)
      const osc3 = this.audioCtx.createOscillator();
      const gain3 = this.audioCtx.createGain();
      osc3.type = 'sine';
      osc3.frequency.setValueAtTime(880, now + 0.25);
      gain3.gain.setValueAtTime(0.2, now + 0.25);
      gain3.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
      osc3.connect(gain3);
      gain3.connect(this.audioCtx.destination);
      osc3.start(now + 0.25);
      osc3.stop(now + 0.7);
    } catch (e) {
      console.warn('Web Audio playback error:', e);
    }
  }

  start() {
    if (this.isPlaying) return;
    this.isPlaying = true;
    this.playChime();
    this.intervalId = setInterval(() => {
      this.playChime();
    }, 2200);
  }

  stop() {
    this.isPlaying = false;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}

export const ringtone = new CallRingtone();
